import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { 
  History, Search, Filter, User, Building2, FileText, Shield,
  ChevronLeft, ChevronRight, Activity, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const ACAO_COLORS = {
  "CREATE": "bg-green-500",
  "UPDATE": "bg-blue-500",
  "DELETE": "bg-red-500",
  "LOGIN": "bg-purple-500",
  "LOGOUT": "bg-slate-500",
  "APPROVE": "bg-emerald-500",
  "REJECT": "bg-orange-500"
};

const ENTIDADE_ICONS = {
  "instituicao": Building2,
  "missao": Activity,
  "voluntario": User,
  "documento": FileText,
  "user": User
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 20;
  
  // Filters
  const [filterEntidade, setFilterEntidade] = useState('all');
  const [filterAcao, setFilterAcao] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  useEffect(() => {
    loadData();
  }, [page, filterEntidade, filterAcao, dataInicio, dataFim]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (filterEntidade !== 'all') params.append('entidade', filterEntidade);
      if (filterAcao !== 'all') params.append('acao', filterAcao);
      if (dataInicio) params.append('data_inicio', dataInicio);
      if (dataFim) params.append('data_fim', dataFim);
      
      const res = await apiClient.get(`/admin/audit-logs?${params.toString()}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await apiClient.get('/admin/audit-logs/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      log.user_nome?.toLowerCase().includes(search) ||
      log.descricao?.toLowerCase().includes(search) ||
      log.entidade?.toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-PT', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getAcaoColor = (acao) => {
    const acaoUpper = acao?.toUpperCase() || '';
    if (acaoUpper.includes('CREATE') || acaoUpper.includes('CRIAR')) return ACAO_COLORS.CREATE;
    if (acaoUpper.includes('UPDATE') || acaoUpper.includes('ATUALIZAR') || acaoUpper.includes('EDITAR')) return ACAO_COLORS.UPDATE;
    if (acaoUpper.includes('DELETE') || acaoUpper.includes('REMOVER') || acaoUpper.includes('ELIMINAR')) return ACAO_COLORS.DELETE;
    if (acaoUpper.includes('LOGIN')) return ACAO_COLORS.LOGIN;
    if (acaoUpper.includes('APPROVE') || acaoUpper.includes('APROVAR')) return ACAO_COLORS.APPROVE;
    if (acaoUpper.includes('REJECT') || acaoUpper.includes('REJEITAR')) return ACAO_COLORS.REJECT;
    return 'bg-slate-500';
  };

  const getEntidadeIcon = (entidade) => {
    const entidadeLower = entidade?.toLowerCase() || '';
    for (const [key, Icon] of Object.entries(ENTIDADE_ICONS)) {
      if (entidadeLower.includes(key)) return Icon;
    }
    return FileText;
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
          <History className="w-8 h-8 text-[#0066CC]" />
          Log de Auditoria
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Registo de todas as ações realizadas no sistema
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
                <p className="text-sm text-slate-500">Total de Registos</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{stats.por_acao?.find(a => a.acao?.includes('CREATE'))?.count || 0}</p>
                <p className="text-sm text-slate-500">Criações</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{stats.por_acao?.find(a => a.acao?.includes('UPDATE'))?.count || 0}</p>
                <p className="text-sm text-slate-500">Atualizações</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600">{stats.por_acao?.find(a => a.acao?.includes('DELETE'))?.count || 0}</p>
                <p className="text-sm text-slate-500">Eliminações</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Users */}
      {stats?.top_users?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5" />
              Utilizadores Mais Ativos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {stats.top_users.slice(0, 5).map((user, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-3 py-2 rounded-lg">
                  <User className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">{user.nome || 'Anónimo'}</span>
                  <Badge variant="outline">{user.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Pesquisar utilizador, descrição..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="audit-search-input"
              />
            </div>
            <Select value={filterEntidade} onValueChange={v => { setFilterEntidade(v); setPage(0); }}>
              <SelectTrigger className="w-[160px]" data-testid="filter-entidade">
                <SelectValue placeholder="Entidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Entidades</SelectItem>
                <SelectItem value="instituicao">Instituições</SelectItem>
                <SelectItem value="missao">Missões</SelectItem>
                <SelectItem value="voluntario">Voluntários</SelectItem>
                <SelectItem value="visita">Visitas</SelectItem>
                <SelectItem value="certificado">Certificados</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterAcao} onValueChange={v => { setFilterAcao(v); setPage(0); }}>
              <SelectTrigger className="w-[160px]" data-testid="filter-acao">
                <SelectValue placeholder="Ação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Ações</SelectItem>
                <SelectItem value="CREATE">Criação</SelectItem>
                <SelectItem value="UPDATE">Atualização</SelectItem>
                <SelectItem value="DELETE">Eliminação</SelectItem>
                <SelectItem value="LOGIN">Login</SelectItem>
                <SelectItem value="APPROVE">Aprovação</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <Input
                type="date"
                value={dataInicio}
                onChange={e => { setDataInicio(e.target.value); setPage(0); }}
                className="w-[140px]"
                placeholder="De"
              />
              <span className="text-slate-400">-</span>
              <Input
                type="date"
                value={dataFim}
                onChange={e => { setDataFim(e.target.value); setPage(0); }}
                className="w-[140px]"
                placeholder="Até"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Utilizador</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Entidade</TableHead>
                    <TableHead>Descrição</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                        <Shield className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p>Nenhum registo encontrado</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLogs.map((log, idx) => {
                      const EntidadeIcon = getEntidadeIcon(log.entidade);
                      return (
                        <TableRow key={idx}>
                          <TableCell className="whitespace-nowrap">
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              {formatDate(log.timestamp)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-slate-500" />
                              </div>
                              <span className="font-medium">{log.user_nome || 'Sistema'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${getAcaoColor(log.acao)} text-white`}>
                              {log.acao}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <EntidadeIcon className="w-4 h-4 text-slate-400" />
                              <span className="capitalize">{log.entidade}</span>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-md truncate">
                            {log.descricao}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">
                    A mostrar {page * limit + 1} - {Math.min((page + 1) * limit, total)} de {total} registos
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm">
                      Página {page + 1} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
